import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import uuidv4 from "uuid/v4";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button, toast } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { Modal } from "~/ui/legacyFellowIndex";

import type { DirectReport } from "./index";

import type { RemoveDirectReportModalMutation as MutationType } from "~/__generated__/RemoveDirectReportModalMutation.graphql";

import styles from "./css/Modals.module.css";

const RemoveReportMutation = graphql`
	mutation RemoveDirectReportModalMutation($reportId: ID!) {
		removeDirectReport(reportId: $reportId) {
			user {
				internalAttributes {
					directReports {
						edges {
							node {
								...DirectReports_reportInfo
							}
						}
					}
				}
			}
		}
	}
`;

export function RemoveDirectReportModal({
	onClose,
	refetch,
	report,
}: {
	onClose: () => void;
	refetch: () => void;
	report: DirectReport;
}) {
	const [isLoading, setIsLoading] = React.useState(false);

	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: uuidv4(),
		}),
		[],
	);

	React.useEffect(() => {
		dispatchAnalyticsTrack("[Remove Direct Report Modal] Opened Modal", analyticsProperties);
	}, [analyticsProperties]);

	const onDismiss = React.useCallback(() => {
		dispatchAnalyticsTrack("[Remove Direct Report Modal] Dismissed Modal", analyticsProperties);
		onClose();
	}, [analyticsProperties, onClose]);

	const onRemove = React.useCallback(() => {
		setIsLoading(true);
		commitMutation<MutationType>(environment, {
			mutation: RemoveReportMutation,
			variables: {
				reportId: report.id,
			},
			onCompleted: () => {
				refetch();
				onClose();
				setIsLoading(false);
				dispatchAnalyticsTrack("[Remove Direct Report Modal] Successfully Removed Direct Report", analyticsProperties);
				toast.success(`${report.fullName} is no longer one of your direct reports`, {
					title: "Direct report successfully removed",
				});
			},
		});
	}, [report, refetch, onClose, analyticsProperties]);

	return (
		<Modal onClose={onDismiss}>
			<Modal.Header onClose={onDismiss}>
				<Modal.Title>Remove as direct report</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<p className={styles.bodyText}>Are you sure you want to remove this user from your direct reports?</p>
				<div className={styles.reportDisplay}>
					<Avatar style={{ marginRight: "0.5em" }} user={report} />
					<div className={styles.reportInfo}>
						<p className={styles.name}>{report.fullName}</p>
						<p className={styles.email}>{report.email}</p>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onDismiss}>
					Cancel
				</Button>
				<Button variant="destructive" disabled={isLoading} onClick={onRemove}>
					Remove {isLoading && <FontAwesomeIcon icon={faSpinnerThird} spin />}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
